import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, User, Building2, Phone, MapPin, FileText, Loader2, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TrialSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [appSettings, setAppSettings] = useState({
    app_name: 'Thrive',
    app_logo: ''
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    company_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'Australia',
    abn: ''
  });
  const [agreements, setAgreements] = useState({
    termsOfService: false,
    privacyPolicy: false,
    dataStorage: false,
    emailCommunications: false
  });

  useEffect(() => {
    // Fetch public app settings
    fetch(`${API}/api/app-settings/public`)
      .then(res => res.json())
      .then(data => setAppSettings(data))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAgreementChange = (key, checked) => {
    setAgreements(prev => ({ ...prev, [key]: checked }));
  };

  const allAgreementsChecked = agreements.termsOfService && agreements.privacyPolicy && agreements.dataStorage;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Name, email and password are required');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!allAgreementsChecked) {
      toast.error('Please accept all required agreements to continue');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/trial-signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          company_name: formData.company_name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postal_code,
          country: formData.country,
          abn: formData.abn,
          referral_code: referralCode
        })
      });

      if (res.ok) {
        setSuccess(true);
        toast.success('Check your email to verify and activate your account!');
      } else {
        // Try to parse response body
        let errorMessage = 'Signup failed. Please try again.';
        try {
          const data = await res.json();
          errorMessage = data.detail || errorMessage;
        } catch (e) {
          // If body already read, use status-based message
          if (res.status === 400) {
            errorMessage = 'This email may already be registered. Please login instead or use a different email.';
          }
        }
        
        // Check if email already exists based on message
        if (errorMessage.toLowerCase().includes('already registered') || 
            errorMessage.toLowerCase().includes('already be registered')) {
          toast.error('This email is already registered! Please login instead or use a different email.', { duration: 6000 });
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      // Handle body stream already read error
      if (error.message && error.message.includes('body stream already read')) {
        toast.error('This email may already be registered. Please login instead or use a different email.', { duration: 6000 });
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-800 mb-2">Check Your Email! 📧</h1>
          <p className="text-stone-600 mb-6">
            We've sent a verification link to <strong>{formData.email}</strong>. 
            Click the link to activate your free account.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              📮 Don't see the email? Check your spam folder or{' '}
              <button 
                onClick={async () => {
                  try {
                    await fetch(`${API}/api/auth/resend-verification?email=${formData.email}`, { method: 'POST' });
                    toast.success('Verification email resent!');
                  } catch (e) {
                    toast.error('Failed to resend email');
                  }
                }}
                className="text-amber-600 underline"
              >
                click here to resend
              </button>
            </p>
          </div>
          <Button onClick={() => navigate('/login')} variant="outline" className="w-full">
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white text-center">
          <div className="flex justify-center mb-3">
            {appSettings.app_logo ? (
              <img 
                src={`${API}/api/uploads/${appSettings.app_logo}`}
                alt={appSettings.app_name}
                className="w-16 h-16 object-contain bg-white rounded-xl p-1"
              />
            ) : (
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-xl">
                <span className="text-3xl">🐝</span>
              </div>
            )}
          </div>
          <h1 className="text-3xl font-bold mb-2">Create Your Free Account</h1>
          <p className="opacity-90">Free forever • No credit card required • Upgrade anytime</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8">
          {/* Back Link */}
          <Link to="/login" className="inline-flex items-center text-stone-500 hover:text-stone-700 mb-6">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Login
          </Link>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Account Info */}
            <div className="md:col-span-2">
              <h2 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-600" /> Account Details
              </h2>
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="name">Your Name *</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min 6 characters"
                  className="pl-10"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {/* Organization Info */}
            <div className="md:col-span-2 mt-4">
              <h2 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-600" /> Organization Details (Optional)
              </h2>
            </div>

            <div>
              <Label htmlFor="company_name">Club/Company Name</Label>
              <div className="relative mt-1">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Beekeeping Club Inc."
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="abn">ABN (Optional)</Label>
              <div className="relative mt-1">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="abn"
                  name="abn"
                  value={formData.abn}
                  onChange={handleChange}
                  placeholder="12 345 678 901"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Phone</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="0400 000 000"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Street Address</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Honey Lane"
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Melbourne"
              />
            </div>

            <div>
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="VIC"
              />
            </div>

            <div>
              <Label htmlFor="postal_code">Postcode</Label>
              <Input
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleChange}
                placeholder="3000"
              />
            </div>
          </div>

          {/* Acknowledgements */}
          <div className="mt-8 p-4 bg-stone-50 rounded-lg border border-stone-200">
            <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600" />
              Please review and accept the following:
            </h3>
            
            <div className="space-y-4">
              {/* Terms of Service */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="termsOfService"
                  checked={agreements.termsOfService}
                  onCheckedChange={(checked) => handleAgreementChange('termsOfService', checked)}
                  className="mt-0.5"
                />
                <label htmlFor="termsOfService" className="text-sm text-stone-700 cursor-pointer">
                  <span className="font-medium">
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" className="text-amber-600 hover:text-amber-700 underline">
                      Terms of Service
                    </Link>{' '}
                    *
                  </span>
                  <p className="text-stone-500 text-xs mt-1">
                    By signing up, I agree to the terms and conditions of using this beekeeping management platform.
                  </p>
                </label>
              </div>

              {/* Privacy Policy */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacyPolicy"
                  checked={agreements.privacyPolicy}
                  onCheckedChange={(checked) => handleAgreementChange('privacyPolicy', checked)}
                  className="mt-0.5"
                />
                <label htmlFor="privacyPolicy" className="text-sm text-stone-700 cursor-pointer">
                  <span className="font-medium">
                    I accept the{' '}
                    <Link to="/privacy" target="_blank" className="text-amber-600 hover:text-amber-700 underline">
                      Privacy Policy
                    </Link>{' '}
                    *
                  </span>
                  <p className="text-stone-500 text-xs mt-1">
                    I understand how my personal data will be collected, used, and protected in accordance with the Privacy Policy.
                  </p>
                </label>
              </div>

              {/* Data Storage */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="dataStorage"
                  checked={agreements.dataStorage}
                  onCheckedChange={(checked) => handleAgreementChange('dataStorage', checked)}
                  className="mt-0.5"
                />
                <label htmlFor="dataStorage" className="text-sm text-stone-700 cursor-pointer">
                  <span className="font-medium">I consent to data storage and processing *</span>
                  <p className="text-stone-500 text-xs mt-1">
                    I consent to the storage of my beekeeping data, inspection records, and hive information on secure servers for the purpose of providing this service.
                  </p>
                </label>
              </div>

              {/* Email Communications - Optional */}
              <div className="flex items-start gap-3 pt-2 border-t border-stone-200">
                <Checkbox
                  id="emailCommunications"
                  checked={agreements.emailCommunications}
                  onCheckedChange={(checked) => handleAgreementChange('emailCommunications', checked)}
                  className="mt-0.5"
                />
                <label htmlFor="emailCommunications" className="text-sm text-stone-700 cursor-pointer">
                  <span className="font-medium">I would like to receive updates and tips (Optional)</span>
                  <p className="text-stone-500 text-xs mt-1">
                    Send me beekeeping tips, product updates, and community news. You can unsubscribe at any time.
                  </p>
                </label>
              </div>
            </div>
            
            <p className="text-xs text-stone-500 mt-4">* Required fields</p>
          </div>

          {/* Submit */}
          <div className="mt-8">
            <Button
              type="submit"
              disabled={loading || !allAgreementsChecked}
              className={`w-full h-12 text-lg font-semibold ${
                allAgreementsChecked 
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white'
                  : 'bg-stone-200 text-stone-400 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : !allAgreementsChecked ? (
                'Please accept required agreements'
              ) : (
                '🎉 Create My Free Account'
              )}
            </Button>
            {!allAgreementsChecked && (
              <p className="text-center text-sm text-amber-600 mt-2">
                ↑ Please check all required boxes above to continue
              </p>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TrialSignup;
